import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatGdes, formatApiError, productImageUrl, BACKEND_URL, API } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Edit, Trash2, Upload, ShoppingBag, Package, Camera, ThumbsUp, ThumbsDown, ShieldCheck, ShieldAlert, ShieldQuestion, ExternalLink, Gift, Image as ImageIcon, Building2, DollarSign, Mail, Send, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", description: "", price: 0, compare_at_price: 0, stock: 0, category: "telephones",
  images: [], brand: "", is_new: false, is_bestseller: false, is_promo: false, discount_percent: 0,
  product_type: "standard", available_colors: [],
  unit_of_sale: "unit", technician_enabled: false, technician_price: 0,
  options: [],
};

const CATS = [
  { slug: "telephones", name: "Téléphones" },
  { slug: "audio", name: "Audio" },
  { slug: "electromenager", name: "Électroménager" },
  { slug: "energie-solaire", name: "Énergie solaire" },
  { slug: "batteries", name: "Batteries" },
];

const ProductForm = ({ initial, onClose, onSaved, categories }) => {
  const [form, setForm] = useState(initial ? { ...empty, ...initial } : empty);
  const [imageInput, setImageInput] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const cats = categories && categories.length > 0 ? categories : CATS;

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
      : e.target.type === "number" ? Number(e.target.value)
      : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const addColor = () => setForm((f) => ({ ...f, available_colors: [...(f.available_colors || []), { name: "", hex: "#e11d48" }] }));
  const updateColor = (i, k, v) => setForm((f) => ({
    ...f,
    available_colors: (f.available_colors || []).map((c, idx) => idx === i ? { ...c, [k]: v } : c),
  }));
  const removeColor = (i) => setForm((f) => ({
    ...f,
    available_colors: (f.available_colors || []).filter((_, idx) => idx !== i),
  }));

  const addOption = () => setForm((f) => ({ ...f, options: [...(f.options || []), { name: "", values: [] }] }));
  const updateOption = (i, k, v) => setForm((f) => ({
    ...f,
    options: (f.options || []).map((o, idx) => idx === i ? { ...o, [k]: v } : o),
  }));
  const removeOption = (i) => setForm((f) => ({
    ...f,
    options: (f.options || []).filter((_, idx) => idx !== i),
  }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.product_id) await api.put(`/products/${initial.product_id}`, form);
      else await api.post("/products", form);
      toast.success("Produit enregistré");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      toast.success("Image téléchargée");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <form onSubmit={submit} className="bg-white border rounded-xl p-6 space-y-3" data-testid="product-form">
      <div className="grid md:grid-cols-2 gap-3">
        <input required placeholder="Nom" value={form.name} onChange={upd("name")} className="border rounded px-3 py-2" data-testid="pf-name" />
        <input placeholder="Marque" value={form.brand || ""} onChange={upd("brand")} className="border rounded px-3 py-2" />
        <input type="number" required placeholder="Prix HTG (actuel)" value={form.price} onChange={upd("price")} className="border rounded px-3 py-2" data-testid="pf-price" />
        <input type="number" placeholder="Ancien prix HTG (avant réduction)" value={form.compare_at_price || 0} onChange={upd("compare_at_price")} className="border rounded px-3 py-2" data-testid="pf-compare-price" title="Laissez 0 si aucun ancien prix" />
        <input type="number" required placeholder="Stock" value={form.stock} onChange={upd("stock")} className="border rounded px-3 py-2" data-testid="pf-stock" />
        <select value={form.category} onChange={upd("category")} className="border rounded px-3 py-2 bg-white" data-testid="pf-category">
          {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input type="number" placeholder="Remise % (optionnel)" value={form.discount_percent} onChange={upd("discount_percent")} className="border rounded px-3 py-2" />
        <select value={form.product_type || "standard"} onChange={upd("product_type")} className="border rounded px-3 py-2 bg-white" data-testid="pf-product-type">
          <option value="standard">Produit standard</option>
          <option value="cable">Fil électrique (avec couleurs)</option>
        </select>
      </div>
      {(form.product_type === "cable") && (
        <div className="border rounded-lg p-3 bg-blue-50/40" data-testid="pf-colors-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest font-semibold text-gray-600">Couleurs disponibles (fil +/−)</p>
            <button type="button" onClick={addColor} className="text-xs font-semibold text-[#0052CC] hover:underline" data-testid="pf-add-color">+ Ajouter</button>
          </div>
          {(form.available_colors || []).length === 0 && <p className="text-xs text-gray-500">Aucune couleur. Ex: "Rouge (+)", "Noir (−)".</p>}
          <div className="space-y-2">
            {(form.available_colors || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`pf-color-row-${i}`}>
                <input value={c.name} onChange={(e) => updateColor(i, "name", e.target.value)} placeholder="Rouge (+)" className="flex-1 border rounded px-2 py-1 text-sm" />
                <input type="color" value={c.hex || "#e11d48"} onChange={(e) => updateColor(i, "hex", e.target.value)} className="w-10 h-8 rounded border cursor-pointer" />
                <button type="button" onClick={() => removeColor(i)} className="text-red-600 text-xs">×</button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-2">Le prix « par pied » saisi ci-dessus s'applique à toutes les couleurs. Le client renseigne la longueur souhaitée par couleur.</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3 border rounded-lg p-3 bg-amber-50/40" data-testid="pf-options-block">
        <div className="md:col-span-2">
          <p className="text-xs uppercase tracking-widest font-semibold text-gray-600">Options supplémentaires</p>
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-500">Unité de vente</label>
          <select value={form.unit_of_sale || "unit"} onChange={upd("unit_of_sale")} className="w-full border rounded px-3 py-2 mt-1 bg-white text-sm" data-testid="pf-unit-of-sale">
            <option value="unit">À l'unité</option>
            <option value="case">Par caisse / carton</option>
            <option value="foot">Par pied</option>
            <option value="meter">Par mètre</option>
            <option value="kg">Par kg</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase font-semibold text-gray-500 flex items-center gap-2">
            <input type="checkbox" checked={!!form.technician_enabled} onChange={upd("technician_enabled")} data-testid="pf-technician-enabled" />
            Proposer l'installation par technicien
          </label>
          {form.technician_enabled && (
            <input type="number" min="0" placeholder="Prix technicien HTG" value={form.technician_price || 0} onChange={upd("technician_price")} className="w-full border rounded px-3 py-2 mt-2 text-sm" data-testid="pf-technician-price" />
          )}
        </div>
      </div>
      <div className="border rounded-lg p-3 bg-purple-50/40" data-testid="pf-variants-block">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold text-gray-600">Options du produit (taille, couleur…)</p>
            <p className="text-[11px] text-gray-500">Le client choisira une valeur pour chaque option avant d'ajouter au panier.</p>
          </div>
          <button type="button" onClick={addOption} className="text-xs font-semibold text-purple-700 hover:underline" data-testid="pf-add-option">+ Ajouter une option</button>
        </div>
        {(form.options || []).length === 0 && (
          <p className="text-xs text-gray-500">Aucune option. Ex : « Taille » → S, M, L ou « Couleur » → Noir, Blanc.</p>
        )}
        <div className="space-y-2">
          {(form.options || []).map((opt, i) => (
            <div key={i} className="bg-white border rounded p-2" data-testid={`pf-option-row-${i}`}>
              <div className="flex items-center gap-2">
                <input
                  value={opt.name}
                  onChange={(e) => updateOption(i, "name", e.target.value)}
                  placeholder="Nom de l'option (Taille, Couleur…)"
                  className="flex-1 border rounded px-2 py-1 text-sm"
                  data-testid={`pf-option-name-${i}`}
                />
                <button type="button" onClick={() => removeOption(i)} className="text-red-600 text-xs px-2" data-testid={`pf-option-remove-${i}`}>Supprimer</button>
              </div>
              <input
                value={(opt.values || []).join(", ")}
                onChange={(e) => updateOption(i, "values", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="Valeurs séparées par virgule (ex : S, M, L)"
                className="w-full border rounded px-2 py-1 text-sm mt-2"
                data-testid={`pf-option-values-${i}`}
              />
              {(opt.values || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {opt.values.map((v, k) => (
                    <span key={k} className="text-[11px] bg-purple-100 text-purple-800 rounded-full px-2 py-0.5">{v}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <textarea placeholder="Description" rows={3} value={form.description} onChange={upd("description")} className="w-full border rounded px-3 py-2" />
      <div className="flex flex-wrap gap-3 text-sm">
        <label className="flex items-center gap-1"><input type="checkbox" checked={form.is_new} onChange={upd("is_new")} /> Nouveauté</label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={form.is_bestseller} onChange={upd("is_bestseller")} /> Meilleure vente</label>
        <label className="flex items-center gap-1"><input type="checkbox" checked={form.is_promo} onChange={upd("is_promo")} /> Promo</label>
      </div>
      <div>
        <label className="text-xs uppercase font-semibold tracking-wider text-gray-500">Images</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {form.images.map((img, i) => (
            <div key={i} className="relative w-20 h-20 bg-gray-50 rounded border overflow-hidden">
              <img src={productImageUrl(img)} alt="" className="w-full h-full object-contain p-1" />
              <button type="button" onClick={() => setForm((f) => ({...f, images: f.images.filter((_,k) => k !== i)}))} className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-5 h-5 text-xs">×</button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <input value={imageInput} onChange={(e)=>setImageInput(e.target.value)} placeholder="URL d'image…" className="flex-1 border rounded px-3 py-2 text-sm" />
          <button type="button" onClick={() => { if (imageInput) { setForm((f)=>({...f, images:[...f.images, imageInput]})); setImageInput(""); } }} className="border rounded px-3 py-2 text-sm">+ URL</button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="pf-image-file" />
          <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="je-btn-blue inline-flex items-center gap-1 disabled:opacity-50">
            <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
          </button>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" className="je-btn-primary" data-testid="pf-save">Enregistrer</button>
        <button type="button" onClick={onClose} className="border px-4 py-2 rounded">Annuler</button>
      </div>
    </form>
  );
};

const deliveryImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

const OrderDeliveryCell = ({ order, onUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/orders/${order.order_id}/delivery-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo de livraison enregistrée");
      onUploaded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const fb = order.customer_feedback;
  return (
    <div className="flex flex-col gap-1 text-xs" data-testid={`delivery-cell-${order.order_id}`}>
      {order.delivery_photo ? (
        <button
          type="button"
          onClick={() => setPreview(!preview)}
          className="text-[#0052CC] font-semibold inline-flex items-center gap-1 hover:underline"
          data-testid={`view-delivery-photo-${order.order_id}`}
        >
          <Camera className="w-3.5 h-3.5" /> {preview ? "Masquer" : "Voir photo"}
        </button>
      ) : (
        <span className="text-gray-400 inline-flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Aucune photo</span>
      )}
      {preview && order.delivery_photo && (
        <img src={deliveryImageUrl(order.delivery_photo)} alt="" className="w-24 h-24 object-cover rounded border" />
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onPick}
        className="hidden"
        data-testid={`delivery-photo-input-${order.order_id}`}
      />
      <button
        type="button"
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
        className="text-[10px] uppercase tracking-widest font-semibold text-gray-600 hover:text-[#0052CC] inline-flex items-center gap-1 disabled:opacity-50"
        data-testid={`upload-delivery-photo-${order.order_id}`}
      >
        <Upload className="w-3 h-3" /> {uploading ? "…" : order.delivery_photo ? "Remplacer" : "Ajouter photo"}
      </button>
      {fb && (
        <div className={`inline-flex items-center gap-1 mt-1 font-semibold ${fb.rating === "up" ? "text-green-700" : "text-red-700"}`} title={fb.comment || ""}>
          {fb.rating === "up" ? <ThumbsUp className="w-3.5 h-3.5" /> : <ThumbsDown className="w-3.5 h-3.5" />}
          <span>{fb.rating === "up" ? "Satisfait" : "Insatisfait"}</span>
        </div>
      )}
      {fb?.comment && <p className="text-[11px] text-gray-600 italic max-w-[180px] truncate" title={fb.comment}>« {fb.comment} »</p>}
    </div>
  );
};

const bannerImageUrl = (u) => u?.startsWith("http") ? u : (u ? `${BACKEND_URL}${u}` : "");

function BannersManager({ banners, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ badge: "", title: "", subtitle: "", cta: "Découvrir", link: "/products", image_url: "", active: true, position: 999 });
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [localBanners, setLocalBanners] = useState(banners);
  const fileRef = useRef(null);

  useEffect(() => { setLocalBanners(banners); }, [banners]);

  const startEdit = (b) => { setEditing(b.banner_id); setForm({ ...b }); };
  const startNew = () => { setEditing("new"); setForm({ badge: "", title: "", subtitle: "", cta: "Découvrir", link: "/products", image_url: "", active: true, position: banners.length, location: "hero" }); };
  const cancel = () => { setEditing(null); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    try {
      if (editing === "new") {
        await api.post("/admin/banners", form);
        toast.success("Bannière créée");
      } else {
        await api.put(`/admin/banners/${editing}`, form);
        toast.success("Bannière mise à jour");
      }
      setEditing(null);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette bannière ?")) return;
    try { await api.delete(`/admin/banners/${id}`); toast.success("Supprimée"); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleActive = async (b) => {
    try { await api.put(`/admin/banners/${b.banner_id}`, { active: !b.active }); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/banners/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // Drag-and-drop reorder
  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const list = [...localBanners];
    const fromIdx = list.findIndex((b) => b.banner_id === dragId);
    const toIdx = list.findIndex((b) => b.banner_id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); return; }
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    setLocalBanners(list);
    setDragId(null);
    try {
      await api.put("/admin/banners/reorder", { order: list.map((b) => b.banner_id) });
      toast.success("Ordre mis à jour");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); onChanged(); }
  };

  return (
    <div>
      <div className="bg-gradient-to-br from-[#0052CC] to-[#003D99] text-white rounded-xl p-5 mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Gestion des bannières</h3>
          <p className="text-sm opacity-90 mt-1">🖱️ Glissez-déposez les cartes pour changer l&apos;ordre. Cliquez pour créer, modifier ou supprimer.</p>
        </div>
        <button onClick={startNew} className="bg-white text-[#0052CC] font-bold px-4 py-2 rounded-lg inline-flex items-center gap-2" data-testid="new-banner-btn">
          <Plus className="w-4 h-4" /> Nouvelle bannière
        </button>
      </div>

      {editing && (
        <div className="bg-white border-2 border-[#0052CC] rounded-xl p-5 mb-4" data-testid="banner-form">
          <h4 className="font-bold mb-3">{editing === "new" ? "Nouvelle bannière" : "Modifier la bannière"}</h4>
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-gray-600">Badge (optionnel)</label>
              <input value={form.badge} onChange={(e)=>setForm({...form,badge:e.target.value})} placeholder="🆕 Nouveau" className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Emplacement</label>
              <select value={form.location || "hero"} onChange={(e)=>setForm({...form,location:e.target.value})} className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="banner-location">
                <option value="hero">🎯 En haut (carrousel principal)</option>
                <option value="footer">📌 En bas de la page d&apos;accueil</option>
              </select></div>
            <div className="md:col-span-2"><label className="text-xs font-semibold text-gray-600">Titre *</label>
              <input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} placeholder="iPhone 15 Pro Max" className="w-full border rounded px-3 py-2 text-sm" data-testid="banner-title-input" /></div>
            <div className="md:col-span-2"><label className="text-xs font-semibold text-gray-600">Sous-titre</label>
              <textarea value={form.subtitle} onChange={(e)=>setForm({...form,subtitle:e.target.value})} rows={2} placeholder="Puissance ultime, système photo Pro" className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Texte du bouton</label>
              <input value={form.cta} onChange={(e)=>setForm({...form,cta:e.target.value})} placeholder="Acheter maintenant" className="w-full border rounded px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Lien</label>
              <input value={form.link} onChange={(e)=>setForm({...form,link:e.target.value})} placeholder="/products?category=telephones" className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
            <div className="md:col-span-2"><label className="text-xs font-semibold text-gray-600">Image</label>
              <div className="flex gap-2">
                <input value={form.image_url} onChange={(e)=>setForm({...form,image_url:e.target.value})} placeholder="URL ou téléversez ci-dessous" className="flex-1 border rounded px-3 py-2 text-sm" />
                <button type="button" onClick={()=>fileRef.current?.click()} disabled={uploading} className="bg-[#0052CC] text-white px-3 rounded inline-flex items-center gap-1 text-sm font-semibold disabled:opacity-50" data-testid="banner-upload-btn">
                  <Upload className="w-4 h-4" /> {uploading ? "…" : "Téléverser"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" />
              </div>
              {form.image_url && <img src={bannerImageUrl(form.image_url)} alt="" className="mt-2 max-h-32 rounded border" />}
            </div>
            <label className="inline-flex items-center gap-2 text-sm md:col-span-2">
              <input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form,active:e.target.checked})} /> Actif (visible sur le site)
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} className="je-btn-primary" data-testid="banner-save-btn">Enregistrer</button>
            <button onClick={cancel} className="border px-4 py-2 rounded">Annuler</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {localBanners.map((b, idx) => (
          <div
            key={b.banner_id}
            draggable
            onDragStart={() => onDragStart(b.banner_id)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(b.banner_id)}
            onDragEnd={() => setDragId(null)}
            className={`bg-white border-2 rounded-xl overflow-hidden transition cursor-move ${b.active ? "" : "opacity-60"} ${dragId === b.banner_id ? "border-[#E3000F] shadow-2xl scale-95" : "border-gray-200"}`}
            data-testid={`banner-card-${b.banner_id}`}
          >
            <div className="relative aspect-[16/9] bg-gray-100">
              {b.image_url && <img src={bannerImageUrl(b.image_url)} alt={b.title} className="w-full h-full object-cover pointer-events-none" />}
              {b.badge && <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">{b.badge}</span>}
              <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-white/90">#{idx + 1}</span>
              <span className={`absolute bottom-2 left-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded ${(b.location || "hero") === "footer" ? "bg-purple-600 text-white" : "bg-blue-600 text-white"}`}>
                {(b.location || "hero") === "footer" ? "📌 Bas" : "🎯 Haut"}
              </span>
              <span className="absolute bottom-2 right-2 bg-white/90 text-gray-600 text-[10px] px-2 py-0.5 rounded inline-flex items-center gap-1">
                ⋮⋮ Glisser
              </span>
            </div>
            <div className="p-3">
              <p className="font-bold text-sm line-clamp-1">{b.title}</p>
              {b.subtitle && <p className="text-xs text-gray-500 line-clamp-2 mt-1">{b.subtitle}</p>}
              <p className="text-[11px] font-mono text-[#0052CC] mt-2 truncate">→ {b.link}</p>
              <div className="flex gap-1.5 mt-3 flex-wrap">
                <button onClick={() => startEdit(b)} className="text-xs bg-[#0052CC] text-white px-3 py-1.5 rounded font-semibold inline-flex items-center gap-1" data-testid={`edit-banner-${b.banner_id}`}>
                  <Edit className="w-3 h-3" /> Modifier
                </button>
                <button onClick={() => toggleActive(b)} className={`text-xs px-3 py-1.5 rounded font-semibold ${b.active ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
                  {b.active ? "Désactiver" : "Activer"}
                </button>
                <button onClick={() => del(b.banner_id)} className="text-xs bg-red-100 text-red-700 px-3 py-1.5 rounded font-semibold inline-flex items-center gap-1" data-testid={`del-banner-${b.banner_id}`}>
                  <Trash2 className="w-3 h-3" /> Supprimer
                </button>
              </div>
            </div>
          </div>
        ))}
        {localBanners.length === 0 && !editing && (
          <div className="col-span-full text-center text-gray-500 py-8 bg-white border rounded-xl">Aucune bannière. Cliquez sur &quot;Nouvelle bannière&quot; pour commencer.</div>
        )}
      </div>
    </div>
  );
}

function BankDetailsForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState(132);
  const [savingRate, setSavingRate] = useState(false);

  useEffect(() => {
    api.get("/exchange-rate").then(({ data }) => setRate(Number(data.htg_per_usd) || 132)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/bank-details", form);
      toast.success("Coordonnées bancaires enregistrées");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const saveRate = async () => {
    setSavingRate(true);
    try {
      await api.put("/admin/exchange-rate", { htg_per_usd: rate });
      toast.success(`Taux mis à jour : 1 USD = ${rate} HTG`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingRate(false); }
  };

  return (
    <div>
      {/* Exchange rate card */}
      <div className="bg-white border rounded-xl p-5 mb-4" data-testid="exchange-rate-card">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-5 h-5 text-[#6D1ED4]" />
          <h3 className="font-bold" style={{ fontFamily: "Outfit" }}>Taux de change Zelle (HTG → USD)</h3>
        </div>
        <p className="text-sm text-gray-500 mb-3">Ce taux est utilisé pour afficher automatiquement le montant équivalent en dollars US au client lorsqu&apos;il choisit Zelle au checkout.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="text-xs font-semibold text-gray-600">1 USD =</label>
            <div className="inline-flex items-center border-2 rounded-md overflow-hidden ml-2">
              <input
                type="number" min="1" step="0.5"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="px-3 py-2 text-sm w-24 outline-none font-bold text-center"
                data-testid="exchange-rate-input"
              />
              <span className="px-2 py-2 text-xs font-bold bg-gray-100 text-gray-700">HTG</span>
            </div>
          </div>
          <button onClick={saveRate} disabled={savingRate} className="je-btn-primary text-sm disabled:opacity-50" data-testid="exchange-rate-save">
            {savingRate ? "…" : "Enregistrer le taux"}
          </button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#0F766E] to-[#065F46] text-white rounded-xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-1"><Building2 className="w-5 h-5" /> <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Compte bancaire</h3></div>
        <p className="text-sm opacity-90">Configurez vos coordonnées bancaires. Elles apparaîtront au checkout dès que le champ <b>Nom de la banque</b> sera rempli.</p>
      </div>
      <div className="bg-white border rounded-xl p-5" data-testid="bank-details-form">
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-gray-600">Nom de la banque</label>
            <input value={form.bank_name} onChange={(e)=>setForm({...form,bank_name:e.target.value})} placeholder="Ex: Sogebank" className="w-full border rounded px-3 py-2 text-sm" data-testid="bank-name-input" /></div>
          <div><label className="text-xs font-semibold text-gray-600">Titulaire du compte</label>
            <input value={form.account_holder} onChange={(e)=>setForm({...form,account_holder:e.target.value})} placeholder="John Elektwo And More Distribution" className="w-full border rounded px-3 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-gray-600">Numéro de compte</label>
            <input value={form.account_number} onChange={(e)=>setForm({...form,account_number:e.target.value})} placeholder="XXXX-XXXX-XXXX" className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
          <div><label className="text-xs font-semibold text-gray-600">IBAN (optionnel)</label>
            <input value={form.iban} onChange={(e)=>setForm({...form,iban:e.target.value})} className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
          <div><label className="text-xs font-semibold text-gray-600">SWIFT / BIC (optionnel)</label>
            <input value={form.swift} onChange={(e)=>setForm({...form,swift:e.target.value})} className="w-full border rounded px-3 py-2 text-sm font-mono" /></div>
          <div className="md:col-span-2"><label className="text-xs font-semibold text-gray-600">Notes / Instructions</label>
            <textarea value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} rows={3} placeholder="Instructions supplémentaires pour le client" className="w-full border rounded px-3 py-2 text-sm" /></div>
        </div>
        <button onClick={save} disabled={saving} className="je-btn-primary mt-4 disabled:opacity-50" data-testid="bank-save-btn">
          {saving ? "Enregistrement…" : "Enregistrer les coordonnées"}
        </button>
      </div>
    </div>
  );
}

function EmailCampaignPanel() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ subject: "", body: "", to: "all", user_id: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Sujet et message requis"); return; }
    if (form.to === "user" && !form.user_id) { toast.error("Choisissez un utilisateur"); return; }
    if (form.to === "all" && !window.confirm(`Envoyer cet email à TOUS les clients (${users.length}) ?`)) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/campaigns/email", form);
      setResult(data);
      toast.success(`Email envoyé : ${data.sent} succès / ${data.failed} échecs`);
      setForm({ ...form, subject: "", body: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="email-campaigns-panel">
      <div className="bg-gradient-to-br from-[#E3000F] to-[#0052CC] text-white rounded-xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-1"><Send className="w-5 h-5" /> <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Campagnes email</h3></div>
        <p className="text-sm opacity-90">Envoyez une promo ou un message à tous vos clients ou à un utilisateur spécifique. Les emails partent de <b>contact@johnelektwo.com</b> avec votre logo et charte graphique.</p>
      </div>

      <div className="bg-white border rounded-xl p-5">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Destinataire</label>
            <select value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value, user_id: "" })} className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="campaign-to">
              <option value="all">📢 Tous les clients ({users.length})</option>
              <option value="user">👤 Un utilisateur précis</option>
            </select>
          </div>
          {form.to === "user" && (
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Choisir l&apos;utilisateur</label>
              <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm bg-white" data-testid="campaign-user">
                <option value="">— Sélectionner —</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.name} — {u.email}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <label className="text-xs font-semibold text-gray-600 mb-1 block">Sujet *</label>
        <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="🎉 Promo Black Friday — Jusqu'à 50% off !" className="w-full border rounded px-3 py-2 text-sm mb-3" data-testid="campaign-subject" />

        <label className="text-xs font-semibold text-gray-600 mb-1 block">Message *</label>
        <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={8} placeholder="Bonjour, cette semaine profitez de -50% sur les batteries..." className="w-full border rounded px-3 py-2 text-sm font-mono" data-testid="campaign-body" />
        <p className="text-[11px] text-gray-500 mt-1">💡 Vous pouvez utiliser du HTML simple ({'<b>gras</b>'}, {'<a href="…">lien</a>'}). Sauts de ligne convertis automatiquement.</p>

        <button onClick={send} disabled={sending} className="je-btn-primary mt-4 disabled:opacity-50 inline-flex items-center gap-2" data-testid="campaign-send-btn">
          <Send className="w-4 h-4" /> {sending ? "Envoi en cours…" : "Envoyer maintenant"}
        </button>

        {result && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
            <b className="text-green-800">Résultat :</b> {result.sent} email(s) envoyé(s) avec succès sur {result.recipients_count} destinataires
            {result.failed > 0 && <span className="text-red-700"> · {result.failed} échec(s)</span>}
          </div>
        )}
      </div>
    </div>
  );
}

const WHEEL_TYPES = [  { value: "nothing", label: "Rien gagné" },
  { value: "spin_again", label: "Tourner encore" },
  { value: "cashback", label: "Cashback" },
  { value: "free_shipping", label: "Livraison offerte" },
  { value: "discount_percent", label: "% Réduction" },
  { value: "product", label: "Produit" },
];

const WheelOptionRow = ({ idx, option, products, onSaved }) => {
  const [form, setForm] = useState(option);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(option);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/wheel-options/${option.option_id}`, {
        label: form.label, type: form.type, value: form.value, color: form.color, weight: form.weight,
      });
      if (form.type === "product") {
        await api.put(`/admin/wheel-options/${option.option_id}/gift-product`, {
          product_id: form.gift_product_id || null,
        });
      }
      toast.success("Enregistré");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t">
      <td className="px-4 py-2 font-bold text-gray-500">{idx + 1}</td>
      <td className="px-3 py-2">
        <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="border rounded px-2 py-1 text-sm w-full" data-testid={`wheel-label-${idx}`} />
      </td>
      <td className="px-3 py-2">
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border rounded px-2 py-1 text-sm bg-white" data-testid={`wheel-type-${idx}`}>
          {WHEEL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        {form.type === "product" ? (
          <select
            value={form.gift_product_id || ""}
            onChange={(e) => setForm({ ...form, gift_product_id: e.target.value })}
            className="border rounded px-2 py-1 text-sm bg-white min-w-[180px]"
            data-testid={`wheel-product-${idx}`}
          >
            <option value="">— Aucun produit lié —</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.name}</option>
            ))}
          </select>
        ) : (
          <input value={form.value || ""} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="ex: 5" className="border rounded px-2 py-1 text-sm w-20" />
        )}
      </td>
      <td className="px-3 py-2">
        <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-10 h-8 border rounded" />
      </td>
      <td className="px-3 py-2 text-right">
        <input type="number" min="0" max="100" value={form.weight} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })} className="border rounded px-2 py-1 text-sm w-16 text-right" />
      </td>
      <td className="px-3 py-2 text-right">
        <button disabled={!dirty || saving} onClick={save} className="bg-[#0052CC] text-white text-xs px-3 py-1 rounded disabled:opacity-30" data-testid={`wheel-save-${idx}`}>
          {saving ? "…" : "Enregistrer"}
        </button>
      </td>
    </tr>
  );
};

const OrderPaymentCell = ({ order, onChanged }) => {
  const method = order.payment_method;
  const status = order.payment_status;

  const methodLabels = { moncash: "MonCash", natcash: "NatCash", cash_on_delivery: "Livraison", demo: "Démo" };
  const statusConf = {
    pending_proof: { Icon: ShieldQuestion, cls: "text-amber-700", label: "Attente preuve" },
    awaiting_verification: { Icon: ShieldQuestion, cls: "text-blue-700", label: "À vérifier" },
    verified: { Icon: ShieldCheck, cls: "text-green-700", label: "Vérifié" },
    rejected: { Icon: ShieldAlert, cls: "text-red-700", label: "Rejeté" },
    not_required: { Icon: ShieldCheck, cls: "text-gray-500", label: "N/A" },
  }[status] || { Icon: ShieldQuestion, cls: "text-gray-500", label: status || "—" };

  const doAction = async (action) => {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt("Motif du rejet (visible par le client) :", "Preuve non lisible / montant incorrect");
      if (reason === null) return;
    }
    try {
      await api.put(`/admin/orders/${order.order_id}/verify-payment`, { action, reason });
      toast.success(action === "verify" ? "Paiement vérifié" : "Paiement rejeté");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const StatusIcon = statusConf.Icon;

  return (
    <div className="text-xs flex flex-col gap-1" data-testid={`payment-cell-${order.order_id}`}>
      <span className="font-semibold">{methodLabels[method] || method}</span>
      <span className={`inline-flex items-center gap-1 font-semibold ${statusConf.cls}`}>
        <StatusIcon className="w-3.5 h-3.5" /> {statusConf.label}
      </span>
      {order.payment_sender_name && (
        <span className="text-[11px] text-gray-600">{order.payment_sender_name}</span>
      )}
      {order.payment_reference && (
        <span className="text-[11px] font-mono text-gray-600">Réf: {order.payment_reference}</span>
      )}
      {order.payment_proof_url && (
        <a
          href={`${BACKEND_URL}${order.payment_proof_url}`}
          target="_blank"
          rel="noreferrer"
          className="text-[#0052CC] inline-flex items-center gap-1 hover:underline"
          data-testid={`view-proof-${order.order_id}`}
        >
          <ExternalLink className="w-3 h-3" /> Voir preuve
        </a>
      )}
      {status === "awaiting_verification" && (
        <div className="flex gap-1 mt-1">
          <button
            onClick={() => doAction("verify")}
            className="px-2 py-0.5 bg-green-600 text-white rounded text-[10px] uppercase tracking-widest font-bold"
            data-testid={`verify-payment-${order.order_id}`}
          >
            Vérifier
          </button>
          <button
            onClick={() => doAction("reject")}
            className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] uppercase tracking-widest font-bold"
            data-testid={`reject-payment-${order.order_id}`}
          >
            Rejeter
          </button>
        </div>
      )}
    </div>
  );
};

function CategoriesManager({ categories, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: "", name: "", icon: "", image_url: "", position: 0, active: true });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const startEdit = (c) => {
    setEditing(c);
    setForm({ slug: c.slug, name: c.name, icon: c.icon || "", image_url: c.image_url || "", position: c.position || 0, active: c.active });
    setAdding(true);
  };

  const reset = () => {
    setForm({ slug: "", name: "", icon: "", image_url: "", position: categories.length, active: true });
    setEditing(null);
    setAdding(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.category_id}`, form);
        toast.success("Catégorie mise à jour");
      } else {
        await api.post("/admin/categories", form);
        toast.success("Catégorie créée");
      }
      onChanged();
      reset();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Supprimer la catégorie "${c.name}" ?`)) return;
    try { await api.delete(`/admin/categories/${c.category_id}`); toast.success("Supprimé"); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${API}/files/${data.path}` }));
      toast.success("Image chargée");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" data-testid="categories-title">Catégories</h2>
          <p className="text-xs text-gray-500 mt-1">Créez, modifiez, réordonnez les catégories affichées côté public.</p>
        </div>
        {!adding && <button onClick={() => setAdding(true)} className="je-btn-primary inline-flex items-center gap-1" data-testid="add-category-btn"><Plus className="w-4 h-4" /> Nouvelle catégorie</button>}
      </div>

      {adding && (
        <form onSubmit={submit} className="bg-white border rounded-xl p-5 space-y-3 mb-6" data-testid="category-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">Nom</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Fils électriques" className="w-full border rounded px-3 py-2 mt-1" data-testid="cat-name" />
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">Slug (URL)</label>
              <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="fils-electriques" className="w-full border rounded px-3 py-2 mt-1 font-mono text-sm" data-testid="cat-slug" />
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">Icône (nom lucide-react, optionnel)</label>
              <input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Ex: Cable" className="w-full border rounded px-3 py-2 mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">Position</label>
              <input type="number" value={form.position} onChange={(e) => setForm({ ...form, position: Number(e.target.value) })} className="w-full border rounded px-3 py-2 mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Image / illustration (optionnel)</label>
            <div className="flex gap-2 mt-1">
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL ou téléversez ci-dessous" className="flex-1 border rounded px-3 py-2 text-sm" />
              <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
              <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="je-btn-blue inline-flex items-center gap-1 disabled:opacity-50">
                <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
              </button>
            </div>
            {form.image_url && (
              <div className="mt-2 w-24 h-24 border rounded overflow-hidden bg-gray-50">
                <img src={form.image_url} alt="" className="w-full h-full object-contain" />
              </div>
            )}
          </div>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (visible publiquement)</label>
          <div className="flex gap-2 pt-2">
            <button type="submit" className="je-btn-primary" data-testid="cat-save">{editing ? "Enregistrer" : "Créer"}</button>
            <button type="button" onClick={reset} className="border px-4 py-2 rounded">Annuler</button>
          </div>
        </form>
      )}

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Image</th>
              <th className="text-left px-3 py-3">Nom</th>
              <th className="text-left px-3 py-3">Slug</th>
              <th className="text-left px-3 py-3">Position</th>
              <th className="text-left px-3 py-3">Statut</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.category_id} className="border-t" data-testid={`category-row-${c.slug}`}>
                <td className="px-4 py-3">
                  {c.image_url ? <img src={c.image_url} alt="" className="w-10 h-10 object-cover rounded" /> : <span className="text-xs text-gray-400">—</span>}
                </td>
                <td className="px-3 py-3 font-semibold">{c.name}</td>
                <td className="px-3 py-3 font-mono text-xs text-gray-600">{c.slug}</td>
                <td className="px-3 py-3">{c.position}</td>
                <td className="px-3 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${c.active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => startEdit(c)} className="text-[#0052CC] mr-3" data-testid={`edit-cat-${c.slug}`}><Edit className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(c)} className="text-red-600" data-testid={`del-cat-${c.slug}`}><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {categories.length === 0 && <tr><td colSpan="6" className="text-center text-gray-500 py-8">Aucune catégorie.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SocialLinksForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/social-links", form);
      toast.success("Réseaux sociaux mis à jour");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const FIELDS = [
    { key: "facebook", label: "Facebook", placeholder: "https://www.facebook.com/johnelektwo" },
    { key: "instagram", label: "Instagram", placeholder: "https://www.instagram.com/johnelektwo" },
    { key: "tiktok", label: "TikTok", placeholder: "https://www.tiktok.com/@johnelektwo" },
    { key: "youtube", label: "YouTube", placeholder: "https://www.youtube.com/@johnelektwo" },
    { key: "whatsapp", label: "WhatsApp", placeholder: "https://wa.me/50932190716" },
    { key: "twitter", label: "Twitter / X", placeholder: "https://twitter.com/johnelektwo" },
    { key: "linkedin", label: "LinkedIn", placeholder: "https://www.linkedin.com/company/johnelektwo" },
  ];

  return (
    <form onSubmit={save} className="bg-white border rounded-xl p-6 max-w-3xl space-y-4" data-testid="social-links-form">
      <div>
        <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Réseaux sociaux</h2>
        <p className="text-xs text-gray-500 mt-1">Ces liens seront affichés en pied de page. Laissez vide pour masquer un réseau.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map(({ key, label, placeholder }) => (
          <div key={key}>
            <label className="text-xs uppercase font-semibold text-gray-500">{label}</label>
            <input
              value={form[key] || ""}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              placeholder={placeholder}
              className="w-full border rounded px-3 py-2 mt-1 text-sm"
              data-testid={`social-${key}`}
            />
          </div>
        ))}
      </div>
      <button type="submit" disabled={saving} className="je-btn-primary disabled:opacity-50" data-testid="save-social-links">
        {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </form>
  );
}

function VideosManager({ videos, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", type: "youtube", url: "", thumbnail: "", position: 0, active: true });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const startEdit = (v) => {
    setEditing(v);
    setForm({ ...v });
    setAdding(true);
  };

  const reset = () => {
    setForm({ title: "", description: "", type: "youtube", url: "", thumbnail: "", position: videos.length, active: true });
    setEditing(null);
    setAdding(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/videos/${editing.video_id}`, form);
        toast.success("Vidéo mise à jour");
      } else {
        await api.post("/admin/videos", form);
        toast.success("Vidéo ajoutée");
      }
      onChanged();
      reset();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (v) => {
    if (!window.confirm(`Supprimer la vidéo "${v.title || v.url}" ?`)) return;
    try { await api.delete(`/admin/videos/${v.video_id}`); toast.success("Supprimé"); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, type: "upload", url: data.url }));
      toast.success("Vidéo téléversée");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" data-testid="videos-title">Vidéos promotionnelles</h2>
          <p className="text-xs text-gray-500 mt-1">Affichez des vidéos YouTube, Vimeo ou téléchargées directement sur la page d'accueil.</p>
        </div>
        {!adding && <button onClick={() => setAdding(true)} className="je-btn-primary inline-flex items-center gap-1" data-testid="add-video-btn"><Plus className="w-4 h-4" /> Nouvelle vidéo</button>}
      </div>

      {adding && (
        <form onSubmit={submit} className="bg-white border rounded-xl p-5 space-y-3 mb-6" data-testid="video-form">
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Titre</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Découvrez nos offres solaires" className="w-full border rounded px-3 py-2 mt-1" data-testid="vid-title" />
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Description (optionnel)</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-gray-500">Source</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[
                { v: "youtube", label: "YouTube" },
                { v: "vimeo", label: "Vimeo" },
                { v: "upload", label: "Fichier uploadé" },
              ].map(({ v, label }) => (
                <button
                  type="button"
                  key={v}
                  onClick={() => setForm({ ...form, type: v })}
                  className={`px-3 py-2 rounded border text-sm font-semibold transition ${form.type === v ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                  data-testid={`vid-type-${v}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {form.type === "upload" ? (
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">Fichier vidéo (mp4, mov, webm, max 100 Mo)</label>
              <div className="flex gap-2 mt-1">
                <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Chemin après téléversement" className="flex-1 border rounded px-3 py-2 text-sm font-mono" />
                <input ref={fileRef} type="file" accept="video/*" onChange={onUpload} className="hidden" data-testid="vid-file-input" />
                <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="je-btn-blue inline-flex items-center gap-1 disabled:opacity-50">
                  <Upload className="w-4 h-4" /> {uploading ? "Envoi…" : "Upload"}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">
                URL {form.type === "youtube" ? "YouTube" : "Vimeo"}
              </label>
              <input
                required
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder={form.type === "youtube" ? "https://www.youtube.com/watch?v=..." : "https://vimeo.com/..."}
                className="w-full border rounded px-3 py-2 mt-1 text-sm font-mono"
                data-testid="vid-url"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">Miniature (URL, optionnel)</label>
              <input value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase font-semibold text-gray-500">Position</label>
              <input type="number" value={form.position} onChange={(e) => setForm({ ...form, position: Number(e.target.value) })} className="w-full border rounded px-3 py-2 mt-1" />
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (visible sur la homepage)</label>

          <div className="flex gap-2 pt-2">
            <button type="submit" className="je-btn-primary" data-testid="vid-save">{editing ? "Enregistrer" : "Créer"}</button>
            <button type="button" onClick={reset} className="border px-4 py-2 rounded">Annuler</button>
          </div>
        </form>
      )}

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Titre</th>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-left px-3 py-3">URL</th>
              <th className="text-left px-3 py-3">Pos.</th>
              <th className="text-left px-3 py-3">Statut</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {videos.map((v) => (
              <tr key={v.video_id} className="border-t" data-testid={`video-row-${v.video_id}`}>
                <td className="px-4 py-3 font-semibold">{v.title || <span className="text-gray-400">Sans titre</span>}</td>
                <td className="px-3 py-3 uppercase text-xs font-bold text-[#0052CC]">{v.type}</td>
                <td className="px-3 py-3 font-mono text-xs text-gray-600 truncate max-w-[280px]">{v.url}</td>
                <td className="px-3 py-3">{v.position}</td>
                <td className="px-3 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${v.active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{v.active ? "Active" : "Inactive"}</span></td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => startEdit(v)} className="text-[#0052CC] mr-3" data-testid={`edit-vid-${v.video_id}`}><Edit className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(v)} className="text-red-600" data-testid={`del-vid-${v.video_id}`}><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {videos.length === 0 && <tr><td colSpan="6" className="text-center text-gray-500 py-8">Aucune vidéo. Cliquez sur "Nouvelle vidéo".</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AmbassadorApplicationsManager() {
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(null);
  const [reviewing, setReviewing] = useState(null); // application being reviewed
  const [reviewForm, setReviewForm] = useState({ code: "", discount_percent: 5, commission_percent: 5, review_notes: "" });

  const refresh = () => api.get("/admin/ambassador-applications").then(({ data }) => setApps(data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openApprove = (a) => {
    setReviewing(a);
    setReviewForm({
      code: (a.name || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8),
      discount_percent: 5,
      commission_percent: 5,
      review_notes: "",
    });
  };

  const approve = async (e) => {
    e.preventDefault();
    if (!reviewing) return;
    setBusy(reviewing.application_id);
    try {
      await api.put(`/admin/ambassador-applications/${reviewing.application_id}`, {
        status: "approved",
        code: reviewForm.code,
        discount_percent: Number(reviewForm.discount_percent),
        commission_percent: Number(reviewForm.commission_percent),
        review_notes: reviewForm.review_notes,
      });
      toast.success("Demande approuvée. Ambassadeur créé et email envoyé.");
      setReviewing(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const reject = async (a) => {
    const notes = window.prompt("Note à envoyer au candidat (facultatif) :", "");
    if (notes === null) return;
    setBusy(a.application_id);
    try {
      await api.put(`/admin/ambassador-applications/${a.application_id}`, {
        status: "rejected",
        review_notes: notes || "",
      });
      toast.success("Demande refusée");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const del = async (a) => {
    if (!window.confirm(`Supprimer la demande de ${a.name} ?`)) return;
    try { await api.delete(`/admin/ambassador-applications/${a.application_id}`); toast.success("Supprimée"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const statusPill = (s) => {
    const map = { pending: "bg-amber-100 text-amber-800", approved: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800" };
    return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${map[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>;
  };

  return (
    <div data-testid="applications-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Demandes d'ambassadeurs</h2>
          <p className="text-xs text-gray-500 mt-1">Étudiez et validez les candidatures reçues via le formulaire public.</p>
        </div>
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Candidat</th>
              <th className="text-left px-3 py-3">Contact</th>
              <th className="text-left px-3 py-3">Profil</th>
              <th className="text-left px-3 py-3">Réseaux</th>
              <th className="text-left px-3 py-3">Statut</th>
              <th className="text-left px-3 py-3">Date</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {apps.map((a) => (
              <tr key={a.application_id} className="border-t align-top" data-testid={`application-row-${a.application_id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{a.name}</div>
                  {a.user_id && <div className="text-[10px] text-green-700 font-semibold">✓ Compte lié</div>}
                </td>
                <td className="px-3 py-3 text-xs">
                  <div>{a.email}</div>
                  <div className="text-gray-500">{a.phone}</div>
                </td>
                <td className="px-3 py-3 uppercase text-xs">{a.profile_type}</td>
                <td className="px-3 py-3 max-w-xs">
                  {a.social_links ? <pre className="text-[10px] whitespace-pre-wrap text-gray-600 line-clamp-4">{a.social_links}</pre> : <span className="text-gray-400 text-xs">—</span>}
                </td>
                <td className="px-3 py-3">{statusPill(a.status)}</td>
                <td className="px-3 py-3 text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {a.status === "pending" && (
                    <>
                      <button disabled={busy===a.application_id} onClick={() => openApprove(a)} className="text-green-600 mr-2 text-xs font-semibold" data-testid={`approve-${a.application_id}`}>✓ Approuver</button>
                      <button disabled={busy===a.application_id} onClick={() => reject(a)} className="text-red-600 mr-2 text-xs font-semibold" data-testid={`reject-${a.application_id}`}>✕ Refuser</button>
                    </>
                  )}
                  <button onClick={() => del(a)} className="text-red-500" data-testid={`del-app-${a.application_id}`}><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {apps.length === 0 && <tr><td colSpan="7" className="text-center text-gray-500 py-8">Aucune demande.</td></tr>}
          </tbody>
        </table>
      </div>

      {reviewing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setReviewing(null)}>
          <form onSubmit={approve} className="bg-white rounded-2xl max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="approve-form">
            <div className="p-5 border-b">
              <h3 className="font-bold" style={{ fontFamily: "Outfit" }}>Approuver {reviewing.name}</h3>
              <p className="text-xs text-gray-500 mt-1">Un ambassadeur sera créé et un email d'accueil envoyé à <b>{reviewing.email}</b>.</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs uppercase font-semibold text-gray-500">Code ambassadeur</label>
                <input required value={reviewForm.code} onChange={(e) => setReviewForm({ ...reviewForm, code: e.target.value.toUpperCase() })} className="w-full border rounded px-3 py-2 mt-1 font-mono" data-testid="approve-code" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500">Réduction client (%)</label>
                  <input required type="number" min="0" max="90" value={reviewForm.discount_percent} onChange={(e) => setReviewForm({ ...reviewForm, discount_percent: e.target.value })} className="w-full border rounded px-3 py-2 mt-1" data-testid="approve-discount" />
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500">Commission (%)</label>
                  <input required type="number" min="0" max="90" value={reviewForm.commission_percent} onChange={(e) => setReviewForm({ ...reviewForm, commission_percent: e.target.value })} className="w-full border rounded px-3 py-2 mt-1" data-testid="approve-commission" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-semibold text-gray-500">Notes internes (facultatif)</label>
                <textarea rows={2} value={reviewForm.review_notes} onChange={(e) => setReviewForm({ ...reviewForm, review_notes: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
              </div>
            </div>
            <div className="p-5 border-t flex gap-2 justify-end">
              <button type="button" onClick={() => setReviewing(null)} className="border px-4 py-2 rounded">Annuler</button>
              <button type="submit" disabled={busy===reviewing.application_id} className="je-btn-primary disabled:opacity-50" data-testid="confirm-approve">Approuver et créer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function AmbassadorsManager() {
  const [ambs, setAmbs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", email: "", phone: "", discount_percent: 5, commission_percent: 0, active: true });
  const [ordersFor, setOrdersFor] = useState(null);

  const refresh = () => api.get("/admin/ambassadors").then(({ data }) => setAmbs(data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const start = () => { setEditing(null); setForm({ name: "", code: "", email: "", phone: "", discount_percent: 5, commission_percent: 0, active: true }); setAdding(true); };
  const startEdit = (a) => { setEditing(a); setForm({ ...a }); setAdding(true); };
  const reset = () => { setEditing(null); setAdding(false); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/ambassadors/${editing.ambassador_id}`, form); toast.success("Ambassadeur mis à jour"); }
      else { await api.post("/admin/ambassadors", form); toast.success("Ambassadeur créé"); }
      refresh(); reset();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (a) => {
    if (!window.confirm(`Supprimer l'ambassadeur "${a.name}" ?`)) return;
    try { await api.delete(`/admin/ambassadors/${a.ambassador_id}`); toast.success("Supprimé"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const toggleActive = async (a) => {
    try { await api.put(`/admin/ambassadors/${a.ambassador_id}`, { active: !a.active }); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const viewOrders = async (a) => {
    try {
      const { data } = await api.get(`/admin/ambassadors/${a.ambassador_id}/orders`);
      setOrdersFor({ amb: a, orders: data });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const buildLink = (code) => `${window.location.origin}/?ref=${code}`;
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copié"); } catch { toast.error("Impossible de copier"); }
  };

  return (
    <div data-testid="ambassadors-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Ambassadeurs</h2>
          <p className="text-xs text-gray-500 mt-1">Gérez vos ambassadeurs, leurs codes de parrainage et leurs commissions.</p>
        </div>
        {!adding && <button onClick={start} className="je-btn-primary inline-flex items-center gap-1" data-testid="add-ambassador-btn"><Plus className="w-4 h-4" /> Nouvel ambassadeur</button>}
      </div>

      {adding && (
        <form onSubmit={submit} className="bg-white border rounded-xl p-5 space-y-3 mb-6" data-testid="ambassador-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-xs uppercase font-semibold text-gray-500">Nom</label>
              <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="w-full border rounded px-3 py-2 mt-1" data-testid="amb-name" /></div>
            <div><label className="text-xs uppercase font-semibold text-gray-500">Code (majuscules + chiffres)</label>
              <input value={form.code} onChange={(e)=>setForm({...form,code:e.target.value.toUpperCase()})} placeholder="Auto si vide" className="w-full border rounded px-3 py-2 mt-1 font-mono" data-testid="amb-code" /></div>
            <div><label className="text-xs uppercase font-semibold text-gray-500">Email</label>
              <input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="w-full border rounded px-3 py-2 mt-1" /></div>
            <div><label className="text-xs uppercase font-semibold text-gray-500">Téléphone</label>
              <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="w-full border rounded px-3 py-2 mt-1" /></div>
            <div><label className="text-xs uppercase font-semibold text-gray-500">Réduction client (%)</label>
              <input type="number" min="0" max="90" value={form.discount_percent} onChange={(e)=>setForm({...form,discount_percent:Number(e.target.value)})} className="w-full border rounded px-3 py-2 mt-1" data-testid="amb-discount" /></div>
            <div><label className="text-xs uppercase font-semibold text-gray-500">Commission ambassadeur (%)</label>
              <input type="number" min="0" max="90" value={form.commission_percent} onChange={(e)=>setForm({...form,commission_percent:Number(e.target.value)})} className="w-full border rounded px-3 py-2 mt-1" /></div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form,active:e.target.checked})} /> Actif</label>
          <div className="flex gap-2 pt-2">
            <button type="submit" className="je-btn-primary" data-testid="amb-save">{editing ? "Enregistrer" : "Créer"}</button>
            <button type="button" onClick={reset} className="border px-4 py-2 rounded">Annuler</button>
          </div>
        </form>
      )}

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Nom</th>
              <th className="text-left px-3 py-3">Code / Lien</th>
              <th className="text-right px-3 py-3">Réduc.</th>
              <th className="text-right px-3 py-3">Comm.</th>
              <th className="text-right px-3 py-3">Visites</th>
              <th className="text-right px-3 py-3">Cmd.</th>
              <th className="text-right px-3 py-3">Ventes</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {ambs.map((a) => (
              <tr key={a.ambassador_id} className="border-t" data-testid={`amb-row-${a.code}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{a.name}</div>
                  <div className="text-xs text-gray-500">{a.email || "—"}</div>
                </td>
                <td className="px-3 py-3">
                  <div className="font-mono font-bold text-[#0052CC]">{a.code}</div>
                  <button onClick={() => copy(buildLink(a.code))} className="text-xs text-gray-500 hover:underline mt-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Copier le lien</button>
                </td>
                <td className="px-3 py-3 text-right">{a.discount_percent}%</td>
                <td className="px-3 py-3 text-right">{a.commission_percent || 0}%</td>
                <td className="px-3 py-3 text-right">{a.visits_count}</td>
                <td className="px-3 py-3 text-right">{a.orders_count}</td>
                <td className="px-3 py-3 text-right font-semibold">{formatGdes(a.sales_total)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button onClick={() => viewOrders(a)} className="text-[#0052CC] mr-2 text-xs" data-testid={`view-orders-${a.code}`}>Commandes</button>
                  <button onClick={() => toggleActive(a)} className={`mr-2 text-xs font-semibold ${a.active ? "text-amber-600" : "text-green-600"}`}>{a.active ? "Désactiver" : "Activer"}</button>
                  <button onClick={() => startEdit(a)} className="text-[#0052CC] mr-2"><Edit className="w-4 h-4 inline" /></button>
                  <button onClick={() => del(a)} className="text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {ambs.length === 0 && <tr><td colSpan="8" className="text-center text-gray-500 py-8">Aucun ambassadeur. Cliquez sur "Nouvel ambassadeur".</td></tr>}
          </tbody>
        </table>
      </div>

      {ordersFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOrdersFor(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b sticky top-0 bg-white flex items-center justify-between">
              <h3 className="font-bold" style={{ fontFamily: "Outfit" }}>Commandes de {ordersFor.amb.name} ({ordersFor.amb.code})</h3>
              <button onClick={() => setOrdersFor(null)} className="text-gray-400 text-xl">×</button>
            </div>
            <div className="p-5 space-y-2">
              {ordersFor.orders.length === 0 && <p className="text-sm text-gray-500 text-center py-6">Aucune commande.</p>}
              {ordersFor.orders.map((o) => (
                <div key={o.order_id} className="border rounded-lg p-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{o.tracking_number}</p>
                    <p className="text-xs text-gray-500">{new Date(o.created_at).toLocaleDateString()} • {o.items?.length} article(s)</p>
                  </div>
                  <span className={`je-status-pill je-status-${o.status}`}>{o.status}</span>
                  <span className="font-bold">{formatGdes(o.total)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Wheel Wins (Gagnants de la Roue) ----------
const WheelWinsPanel = () => {
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | delivered | cancelled

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/wheel-wins");
      setWins(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markDelivered = async (win_id) => {
    try {
      await api.post(`/admin/wheel-wins/${win_id}/mark-delivered`);
      toast.success("Marqué comme remis");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const cancelWin = async (win_id) => {
    if (!window.confirm("Annuler ce gain ? Si c'est un bonus, il sera déduit du solde du client.")) return;
    try {
      await api.post(`/admin/wheel-wins/${win_id}/cancel`);
      toast.success("Gain annulé");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const rows = wins.filter((w) => filter === "all" ? true : (w.status || "pending") === filter);

  const stats = {
    total: wins.length,
    pending: wins.filter((w) => (w.status || "pending") === "pending").length,
    delivered: wins.filter((w) => w.status === "delivered").length,
    cancelled: wins.filter((w) => w.status === "cancelled").length,
  };

  const statusBadge = (s) => {
    const st = s || "pending";
    const map = {
      pending: "bg-amber-100 text-amber-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    const labels = { pending: "À remettre", delivered: "Remis", cancelled: "Annulé" };
    return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[st]}`}>{labels[st]}</span>;
  };

  const fmtType = (t) => ({
    bonus: "Bonus HTG",
    credit: "Crédit",
    money: "Cashback",
    discount_percent: "Remise %",
    free_shipping: "Livraison gratuite",
    cashback: "Cashback",
    product: "Produit cadeau",
    spin_again: "Rejouer",
    nothing: "Aucun gain",
  }[t] || t || "-");

  return (
    <div data-testid="wheel-wins-panel">
      <div className="bg-gradient-to-br from-[#0052CC] to-[#E3000F] text-white rounded-xl p-5 mb-4">
        <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>🏆 Gagnants de la Roue</h3>
        <p className="text-sm opacity-90 mt-1">Suivez tous les prix remportés par vos clients. Marquez les bonus comme remis ou annulez-les.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <button onClick={() => setFilter("all")} className={`text-left border rounded-xl p-3 transition ${filter === "all" ? "border-[#0052CC] bg-[#0052CC]/5" : "bg-white"}`} data-testid="wins-filter-all">
          <p className="text-xs text-gray-500 uppercase font-semibold">Total</p>
          <p className="text-2xl font-extrabold">{stats.total}</p>
        </button>
        <button onClick={() => setFilter("pending")} className={`text-left border rounded-xl p-3 transition ${filter === "pending" ? "border-amber-500 bg-amber-50" : "bg-white"}`} data-testid="wins-filter-pending">
          <p className="text-xs text-gray-500 uppercase font-semibold">À remettre</p>
          <p className="text-2xl font-extrabold text-amber-600">{stats.pending}</p>
        </button>
        <button onClick={() => setFilter("delivered")} className={`text-left border rounded-xl p-3 transition ${filter === "delivered" ? "border-green-500 bg-green-50" : "bg-white"}`} data-testid="wins-filter-delivered">
          <p className="text-xs text-gray-500 uppercase font-semibold">Remis</p>
          <p className="text-2xl font-extrabold text-green-600">{stats.delivered}</p>
        </button>
        <button onClick={() => setFilter("cancelled")} className={`text-left border rounded-xl p-3 transition ${filter === "cancelled" ? "border-red-500 bg-red-50" : "bg-white"}`} data-testid="wins-filter-cancelled">
          <p className="text-xs text-gray-500 uppercase font-semibold">Annulés</p>
          <p className="text-2xl font-extrabold text-red-600">{stats.cancelled}</p>
        </button>
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-3 py-3">Client</th>
              <th className="text-left px-3 py-3">Prix</th>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-right px-3 py-3">Valeur</th>
              <th className="text-center px-3 py-3">Statut</th>
              <th className="text-right px-3 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="text-center text-gray-400 py-8">Chargement…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="7" className="text-center text-gray-500 py-8">Aucun gagnant pour ce filtre.</td></tr>
            )}
            {rows.map((w) => (
              <tr key={w.win_id} className="border-t hover:bg-gray-50/60" data-testid={`wins-row-${w.win_id}`}>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {new Date(w.created_at).toLocaleDateString("fr-FR")}
                  <br />
                  <span className="text-[11px] text-gray-400">{new Date(w.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                </td>
                <td className="px-3 py-3">
                  <p className="font-semibold text-gray-900">{w.user_name || "—"}</p>
                  <p className="text-[11px] text-gray-500">{w.user_email || "—"}</p>
                </td>
                <td className="px-3 py-3 font-semibold">{w.prize_label}</td>
                <td className="px-3 py-3 text-xs text-gray-600">{fmtType(w.prize_type)}</td>
                <td className="px-3 py-3 text-right font-mono text-sm">
                  {w.prize_type === "bonus" || w.prize_type === "credit" || w.prize_type === "money"
                    ? formatGdes(Number(w.prize_value) || 0)
                    : (w.prize_value || "-")}
                </td>
                <td className="px-3 py-3 text-center">{statusBadge(w.status)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {(w.status || "pending") === "pending" ? (
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => markDelivered(w.win_id)}
                        className="text-[11px] bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded font-semibold"
                        data-testid={`wins-deliver-${w.win_id}`}
                      >
                        Marquer remis
                      </button>
                      <button
                        onClick={() => cancelWin(w.win_id)}
                        className="text-[11px] bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded font-semibold"
                        data-testid={`wins-cancel-${w.win_id}`}
                      >
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <span className="text-[11px] text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function AdminPage() {  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promos, setPromos] = useState([]);
  const [fees, setFees] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", discount_percent: 10, max_uses: 0, active: true });
  const [newFee, setNewFee] = useState({ department: "Ouest", city: "", fee: 0, estimated_days: "" });
  const [rewardProducts, setRewardProducts] = useState([]);
  const [selectedRewardId, setSelectedRewardId] = useState("");
  const [wheelOptions, setWheelOptions] = useState([]);
  const [banners, setBanners] = useState([]);
  const [bankDetails, setBankDetails] = useState({ bank_name: "", account_holder: "", account_number: "", iban: "", swift: "", notes: "" });
  const [categories, setCategories] = useState([]);
  const [socialLinks, setSocialLinks] = useState({ facebook: "", instagram: "", tiktok: "", youtube: "", whatsapp: "", twitter: "", linkedin: "" });
  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const [videos, setVideos] = useState([]);

  const refresh = () => {
    api.get("/products", { params: { limit: 500 } }).then(({data})=>setProducts(data));
    api.get("/admin/orders").then(({data})=>setOrders(data)).catch(()=>{});
    api.get("/admin/promo-codes").then(({data})=>setPromos(data)).catch(()=>{});
    api.get("/admin/delivery-fees").then(({data})=>setFees(data)).catch(()=>{});
    api.get("/admin/reward-products").then(({data})=>setRewardProducts(data)).catch(()=>{});
    api.get("/admin/wheel-options").then(({data})=>setWheelOptions(data)).catch(()=>{});
    api.get("/admin/banners").then(({data})=>setBanners(data)).catch(()=>{});
    api.get("/admin/bank-details").then(({data})=>setBankDetails(data)).catch(()=>{});
    api.get("/admin/categories").then(({data})=>setCategories(data)).catch(()=>{});
    api.get("/social-links").then(({data})=>setSocialLinks(data)).catch(()=>{});
    api.get("/admin/videos").then(({data})=>setVideos(data)).catch(()=>{});
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  if (loading) return <div className="je-container py-20 text-center text-gray-500">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <div className="je-container py-20 text-center"><p className="text-gray-500">Accès réservé aux administrateurs.</p></div>;

  const del = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Supprimé"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const updateOrder = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); toast.success("Mis à jour"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="je-container py-10" data-testid="admin-page">
      <h1 className="text-3xl md:text-4xl font-bold" style={{fontFamily:"Outfit"}}>Tableau de bord admin</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white border rounded-xl p-4"><Package className="w-5 h-5 text-[#0052CC]" /><p className="text-xs text-gray-500 mt-2 uppercase tracking-widest font-semibold">Produits</p><p className="text-2xl font-extrabold">{products.length}</p></div>
        <div className="bg-white border rounded-xl p-4"><ShoppingBag className="w-5 h-5 text-[#E3000F]" /><p className="text-xs text-gray-500 mt-2 uppercase tracking-widest font-semibold">Commandes</p><p className="text-2xl font-extrabold">{orders.length}</p></div>
        <div className="bg-white border rounded-xl p-4"><p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Stock faible</p><p className="text-2xl font-extrabold text-amber-600">{products.filter(p=>p.stock<5).length}</p></div>
        <div className="bg-white border rounded-xl p-4"><p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">CA total</p><p className="text-2xl font-extrabold">{formatGdes(orders.reduce((s,o)=>s+o.total,0))}</p></div>
      </div>

      <Tabs defaultValue="products" className="mt-8">
        <div className="flex flex-wrap items-center gap-2">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="products" data-testid="tab-products">Produits</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Commandes</TabsTrigger>
            <TabsTrigger value="banners" data-testid="tab-banners"><ImageIcon className="w-4 h-4 mr-1" /> Bannières</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">📂 Catégories</TabsTrigger>
            <TabsTrigger value="bank" data-testid="tab-bank"><Building2 className="w-4 h-4 mr-1" /> Compte bancaire</TabsTrigger>
          </TabsList>
          <button
            type="button"
            onClick={() => setShowMoreTabs((v) => !v)}
            aria-expanded={showMoreTabs}
            className="ml-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0052CC] hover:text-[#003D99] bg-white border border-[#0052CC]/30 rounded-full px-3 py-1.5 transition"
            data-testid="admin-toggle-more"
          >
            {showMoreTabs ? "Voir moins" : "Voir plus"}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${showMoreTabs ? "rotate-180" : ""}`} />
          </button>
        </div>

        <div
          className={`grid transition-all duration-500 ease-out ${showMoreTabs ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}
          data-testid="admin-more-tabs"
          aria-hidden={!showMoreTabs}
        >
          <div className="overflow-hidden">
            <TabsList className="flex flex-wrap h-auto gap-1 p-1 bg-blue-50/50">
              <TabsTrigger value="promos" data-testid="tab-promos">Codes promo</TabsTrigger>
              <TabsTrigger value="fees" data-testid="tab-fees">Frais de livraison</TabsTrigger>
              <TabsTrigger value="rewards" data-testid="tab-rewards"><Gift className="w-4 h-4 mr-1" /> Récompenses</TabsTrigger>
              <TabsTrigger value="wheel" data-testid="tab-wheel">🎡 Roue</TabsTrigger>
              <TabsTrigger value="wheel-wins" data-testid="tab-wheel-wins">🏆 Gagnants Roue</TabsTrigger>
              <TabsTrigger value="emails" data-testid="tab-emails"><Mail className="w-4 h-4 mr-1" /> Emails</TabsTrigger>
              <TabsTrigger value="social" data-testid="tab-social">🔗 Réseaux</TabsTrigger>
              <TabsTrigger value="videos" data-testid="tab-videos">🎬 Vidéos</TabsTrigger>
              <TabsTrigger value="ambassadors" data-testid="tab-ambassadors">🎯 Ambassadeurs</TabsTrigger>
              <TabsTrigger value="applications" data-testid="tab-applications">📝 Demandes</TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="products" className="mt-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Catalogue</h2>
            <button onClick={() => setAdding(true)} className="je-btn-primary inline-flex items-center gap-1" data-testid="add-product-btn"><Plus className="w-4 h-4" /> Nouveau</button>
          </div>
          {(adding || editing) && (
            <div className="mb-6">
              <ProductForm initial={editing} onClose={() => { setEditing(null); setAdding(false); }} onSaved={refresh} categories={categories} />
            </div>
          )}
          <div className="bg-white border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-4 py-3">Produit</th><th className="text-left px-3 py-3">Cat.</th><th className="text-right px-3 py-3">Prix</th><th className="text-right px-3 py-3">Stock</th><th className="px-3 py-3"></th></tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.product_id} className="border-t">
                    <td className="px-4 py-3 flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-50 rounded flex items-center justify-center overflow-hidden">
                        <img src={productImageUrl(p.images?.[0])} alt="" className="max-h-full max-w-full object-contain p-1" />
                      </div>
                      <span className="font-semibold">{p.name}</span>
                    </td>
                    <td className="px-3 py-3 text-gray-500">{p.category}</td>
                    <td className="px-3 py-3 text-right font-semibold">{formatGdes(p.price)}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${p.stock<5 ? "text-amber-600" : ""}`}>{p.stock}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => setEditing(p)} className="text-[#0052CC] mr-3" data-testid={`edit-${p.product_id}`}><Edit className="w-4 h-4 inline" /></button>
                      <button onClick={() => del(p.product_id)} className="text-red-600" data-testid={`del-${p.product_id}`}><Trash2 className="w-4 h-4 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-5">
          <div className="bg-white border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-4 py-3">N° Suivi</th><th className="text-left px-3 py-3">Client</th><th className="text-right px-3 py-3">Total</th><th className="px-3 py-3 text-left">Statut</th><th className="px-3 py-3 text-left">Paiement</th><th className="px-3 py-3 text-left">Livraison</th><th></th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-t align-top">
                    <td className="px-4 py-3 font-bold">{o.tracking_number}</td>
                    <td className="px-3 py-3">{o.shipping_address.full_name}<br/><span className="text-xs text-gray-500">{o.shipping_address.city}</span></td>
                    <td className="px-3 py-3 text-right font-semibold">{formatGdes(o.total)}</td>
                    <td className="px-3 py-3">
                      <span className={`je-status-pill je-status-${o.status}`}>{o.status}</span>
                    </td>
                    <td className="px-3 py-3">
                      <OrderPaymentCell order={o} onChanged={refresh} />
                    </td>
                    <td className="px-3 py-3">
                      <OrderDeliveryCell order={o} onUploaded={refresh} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <select value={o.status} onChange={(e) => updateOrder(o.order_id, e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid={`order-status-${o.order_id}`}>
                        <option value="pending">En attente</option>
                        <option value="processing">En traitement</option>
                        <option value="delivered">Livré</option>
                        <option value="cancelled">Annulé</option>
                      </select>
                      <a
                        href={`${API}/orders/track/${o.tracking_number}/invoice`}
                        target="_blank"
                        rel="noreferrer"
                        className="block mt-2 text-[10px] uppercase tracking-widest font-semibold text-[#0052CC] hover:underline"
                        data-testid={`admin-invoice-${o.order_id}`}
                      >
                        🧾 Facture A4
                      </a>
                      <a
                        href={`${API}/orders/track/${o.tracking_number}/label?format=80mm`}
                        target="_blank"
                        rel="noreferrer"
                        className="block mt-1 text-[10px] uppercase tracking-widest font-semibold text-[#E3000F] hover:underline"
                        data-testid={`admin-label-${o.order_id}`}
                      >
                        🏷️ Étiquette 80mm
                      </a>
                      <a
                        href={`${API}/orders/track/${o.tracking_number}/label?format=4x6`}
                        target="_blank"
                        rel="noreferrer"
                        className="block mt-1 text-[10px] uppercase tracking-widest font-semibold text-gray-600 hover:underline"
                        data-testid={`admin-label-4x6-${o.order_id}`}
                      >
                        🏷️ Étiquette 4×6"
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(`Supprimer définitivement la commande ${o.tracking_number} ?\nCette action est irréversible.`)) return;
                          try {
                            await api.delete(`/admin/orders/${o.order_id}`);
                            toast.success("Commande supprimée");
                            refresh();
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        className="block mt-2 text-[10px] uppercase tracking-widest font-semibold text-red-600 hover:underline"
                        data-testid={`admin-delete-order-${o.order_id}`}
                      >
                        🗑️ Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="promos" className="mt-5">
          <div className="bg-white border rounded-xl p-5 mb-4">
            <h3 className="font-bold mb-3">Créer un code promo</h3>
            <div className="grid md:grid-cols-4 gap-3">
              <input placeholder="CODE (ex: NOEL2026)" value={newCode.code} onChange={(e)=>setNewCode({...newCode, code: e.target.value.toUpperCase()})} className="border rounded px-3 py-2" data-testid="promo-code" />
              <input type="number" placeholder="Réduction %" value={newCode.discount_percent} onChange={(e)=>setNewCode({...newCode, discount_percent: Number(e.target.value)})} className="border rounded px-3 py-2" data-testid="promo-discount" />
              <input type="number" placeholder="Utilisations max (0=illimité)" value={newCode.max_uses} onChange={(e)=>setNewCode({...newCode, max_uses: Number(e.target.value)})} className="border rounded px-3 py-2" />
              <button
                onClick={async () => {
                  try { await api.post("/admin/promo-codes", newCode); toast.success("Code créé"); setNewCode({ code:"", discount_percent:10, max_uses:0, active:true }); refresh(); }
                  catch (err) { toast.error(formatApiError(err)); }
                }}
                className="je-btn-primary"
                data-testid="promo-create"
              >Créer le code</button>
            </div>
          </div>
          <div className="bg-white border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-4 py-3">Code</th><th className="text-right px-3 py-3">Réduction</th><th className="text-right px-3 py-3">Utilisé</th><th className="text-right px-3 py-3">Max</th><th></th></tr>
              </thead>
              <tbody>
                {promos.map((p) => (
                  <tr key={p.code} className="border-t">
                    <td className="px-4 py-3 font-bold text-[#0052CC]">{p.code}</td>
                    <td className="px-3 py-3 text-right font-semibold">-{p.discount_percent}%</td>
                    <td className="px-3 py-3 text-right">{p.uses_count || 0}</td>
                    <td className="px-3 py-3 text-right">{p.max_uses || "∞"}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={async ()=>{ if (window.confirm("Supprimer ?")) { await api.delete(`/admin/promo-codes/${p.code}`); refresh(); } }} className="text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                    </td>
                  </tr>
                ))}
                {promos.length === 0 && <tr><td colSpan="5" className="text-center text-gray-500 py-8">Aucun code promo. Créez-en un ci-dessus.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="fees" className="mt-5">
          <div className="bg-white border rounded-xl p-5 mb-4">
            <h3 className="font-bold mb-3">Ajouter un frais de livraison</h3>
            <div className="grid md:grid-cols-5 gap-3">
              <select value={newFee.department} onChange={(e)=>setNewFee({...newFee, department:e.target.value})} className="border rounded px-3 py-2 bg-white" data-testid="fee-dept">
                {["Ouest","Nord","Nord-Est","Nord-Ouest","Sud","Sud-Est","Artibonite","Centre","Grand'Anse","Nippes"].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <input placeholder="Ville (optionnel)" value={newFee.city} onChange={(e)=>setNewFee({...newFee, city:e.target.value})} className="border rounded px-3 py-2" data-testid="fee-city" />
              <input type="number" placeholder="Frais HTG" value={newFee.fee} onChange={(e)=>setNewFee({...newFee, fee:Number(e.target.value)})} className="border rounded px-3 py-2" data-testid="fee-amount" />
              <input placeholder="Délai (ex: 2-5j)" value={newFee.estimated_days} onChange={(e)=>setNewFee({...newFee, estimated_days:e.target.value})} className="border rounded px-3 py-2" />
              <button onClick={async () => {
                try { await api.post("/admin/delivery-fees", { ...newFee, city: newFee.city || null }); toast.success("Frais créé"); setNewFee({ department:"Ouest", city:"", fee:0, estimated_days:"" }); refresh(); }
                catch (err) { toast.error(formatApiError(err)); }
              }} className="je-btn-primary" data-testid="fee-create">Créer</button>
            </div>
            <p className="text-xs text-gray-500 mt-2">💡 Si vous laissez la ville vide, le tarif s&apos;applique à tout le département. Une ville spécifique surpasse le tarif département.</p>
          </div>
          <div className="bg-white border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr><th className="text-left px-4 py-3">Département</th><th className="text-left px-3 py-3">Ville</th><th className="text-right px-3 py-3">Frais</th><th className="text-left px-3 py-3">Délai</th><th></th></tr>
              </thead>
              <tbody>
                {fees.map((f) => (
                  <tr key={f.fee_id} className="border-t">
                    <td className="px-4 py-3 font-semibold">{f.department}</td>
                    <td className="px-3 py-3 text-gray-700">{f.city || <span className="text-gray-400 italic">Tout le département</span>}</td>
                    <td className="px-3 py-3 text-right font-bold text-[#0052CC]">{formatGdes(f.fee)}</td>
                    <td className="px-3 py-3 text-gray-500">{f.estimated_days || "—"}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={async () => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/delivery-fees/${f.fee_id}`); refresh(); } }} className="text-red-600" data-testid={`del-fee-${f.fee_id}`}><Trash2 className="w-4 h-4 inline" /></button>
                    </td>
                  </tr>
                ))}
                {fees.length === 0 && <tr><td colSpan="5" className="text-center text-gray-500 py-8">Aucun frais configuré. Ajoutez-en un ci-dessus.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="rewards" className="mt-5">
          <div className="bg-gradient-to-br from-[#0052CC] to-[#003D99] text-white rounded-xl p-5 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="w-5 h-5" />
              <p className="text-xs uppercase tracking-widest font-semibold opacity-90">Programme fidélité</p>
            </div>
            <h3 className="text-xl font-bold" style={{fontFamily:"Outfit"}}>Configuration des cadeaux offerts</h3>
            <p className="text-sm opacity-80 mt-1">Chaque client débloque 1 cadeau à choisir toutes les <b>3 commandes livrées</b>. Ajoutez ci-dessous les produits que vous souhaitez proposer gratuitement.</p>
          </div>

          <div className="bg-white border rounded-xl p-5 mb-4">
            <h3 className="font-bold mb-3">Ajouter un produit à la liste des récompenses</h3>
            <div className="flex flex-wrap gap-2">
              <select
                value={selectedRewardId}
                onChange={(e) => setSelectedRewardId(e.target.value)}
                className="border rounded px-3 py-2 bg-white flex-1 min-w-[280px]"
                data-testid="reward-product-select"
              >
                <option value="">— Choisir un produit —</option>
                {products
                  .filter((p) => !rewardProducts.find((r) => r.product_id === p.product_id))
                  .map((p) => (
                    <option key={p.product_id} value={p.product_id}>
                      {p.name} · {formatGdes(p.price)} · Stock: {p.stock}
                    </option>
                  ))}
              </select>
              <button
                onClick={async () => {
                  if (!selectedRewardId) return;
                  try {
                    await api.post("/admin/reward-products", { product_id: selectedRewardId });
                    toast.success("Ajouté aux récompenses");
                    setSelectedRewardId("");
                    refresh();
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
                disabled={!selectedRewardId}
                className="je-btn-primary disabled:opacity-50"
                data-testid="add-reward-product"
              >
                <Plus className="w-4 h-4 inline mr-1" /> Ajouter
              </button>
            </div>
          </div>

          <div className="bg-white border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Produit</th>
                  <th className="text-right px-3 py-3">Valeur</th>
                  <th className="text-right px-3 py-3">Stock</th>
                  <th className="text-left px-3 py-3">Ajouté le</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rewardProducts.map((r) => (
                  <tr key={r.product_id} className="border-t">
                    <td className="px-4 py-3 flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-50 rounded flex items-center justify-center overflow-hidden">
                        <img src={productImageUrl(r.product?.images?.[0])} alt="" className="max-h-full max-w-full object-contain p-1" />
                      </div>
                      <span className="font-semibold">{r.product?.name}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-semibold">{formatGdes(r.product?.price || 0)}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${(r.product?.stock || 0) < 5 ? "text-amber-600" : ""}`}>{r.product?.stock ?? 0}</td>
                    <td className="px-3 py-3 text-gray-500">{new Date(r.added_at).toLocaleDateString()}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={async () => {
                          if (!window.confirm("Retirer ce produit de la liste des récompenses ?")) return;
                          try { await api.delete(`/admin/reward-products/${r.product_id}`); toast.success("Retiré"); refresh(); }
                          catch (err) { toast.error(formatApiError(err)); }
                        }}
                        className="text-red-600"
                        data-testid={`del-reward-${r.product_id}`}
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
                {rewardProducts.length === 0 && (
                  <tr><td colSpan="5" className="text-center text-gray-500 py-8">Aucun produit-récompense configuré. Ajoutez-en un ci-dessus.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="wheel" className="mt-5">
          <div className="bg-gradient-to-br from-[#E3000F] to-[#0052CC] text-white rounded-xl p-5 mb-4">
            <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>🎡 Configuration de la Roue de la Chance</h3>            <p className="text-sm opacity-90 mt-1">6 options tirées au sort, chaque client peut jouer <b>1 fois par jour</b>. Le poids influence la probabilité de tirage (1 = neutre, 3 = plus fréquent, 0 = jamais).</p>
          </div>

          <div className="bg-white border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-3 py-3">Libellé</th>
                  <th className="text-left px-3 py-3">Type</th>
                  <th className="text-left px-3 py-3">Valeur / Produit</th>
                  <th className="text-left px-3 py-3">Couleur</th>
                  <th className="text-right px-3 py-3">Poids</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {wheelOptions.map((o, idx) => (
                  <WheelOptionRow key={o.option_id} idx={idx} option={o} products={products} onSaved={refresh} />
                ))}
                {wheelOptions.length === 0 && (
                  <tr><td colSpan="7" className="text-center text-gray-500 py-8">Chargement des options par défaut…</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="wheel-wins" className="mt-5">
          <WheelWinsPanel />
        </TabsContent>

        <TabsContent value="banners" className="mt-5">
          <BannersManager banners={banners} onChanged={refresh} />
        </TabsContent>

        <TabsContent value="bank" className="mt-5">
          <BankDetailsForm initial={bankDetails} onSaved={refresh} />
        </TabsContent>

        <TabsContent value="emails" className="mt-5">
          <EmailCampaignPanel />
        </TabsContent>

        <TabsContent value="categories" className="mt-5">
          <CategoriesManager categories={categories} onChanged={refresh} />
        </TabsContent>

        <TabsContent value="social" className="mt-5">
          <SocialLinksForm initial={socialLinks} onSaved={refresh} />
        </TabsContent>

        <TabsContent value="videos" className="mt-5">
          <VideosManager videos={videos} onChanged={refresh} />
        </TabsContent>

        <TabsContent value="ambassadors" className="mt-5">
          <AmbassadorsManager />
        </TabsContent>

        <TabsContent value="applications" className="mt-5">
          <AmbassadorApplicationsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}
